/**
 * Live phone-number mask for the login national-number field. Cosmetic only — the server strips
 * non-digits when it assembles the identifier ("+1" + digits), so this never changes what gets
 * submitted; worst case it just looks off.
 *
 * Digit-driven by design: on every change we extract the digits and re-render the format from
 * scratch, which is why pasting any format just works ("+1 (281) 333-4444", "281.333.4444",
 * "12813334444" all collapse to the same digits, then re-render). US-only (+1) like the rest of
 * login, so the grouping is always (XXX) XXX-XXXX; the dial code lives in the static "+1" chip
 * next to the field and is never typed here.
 */
export class PhoneMask {
    static NANP_LEN = 10;
    static init() {
        const input = document.getElementById('nationalNumber');
        if (!input)
            return;
        input.addEventListener('input', () => PhoneMask.reformat(input));
        // Format anything the browser pre-filled.
        PhoneMask.reformat(input);
    }
    /** Reformat in place, keeping the caret at the same digit offset so mid-edits don't jump. */
    static reformat(input) {
        const caret = input.selectionStart ?? input.value.length;
        const digitsBefore = PhoneMask.digits(input.value.slice(0, caret)).length;
        input.value = PhoneMask.format(PhoneMask.digits(input.value));
        const pos = PhoneMask.caretForDigit(input.value, digitsBefore);
        input.setSelectionRange(pos, pos);
    }
    static digits(s) {
        return s.replace(/\D/g, '');
    }
    /** (XXX) XXX-XXXX, emitted progressively as digits arrive. */
    static format(digits) {
        // A pasted E.164 +1 number carries a leading country-code "1" (NANP national numbers never
        // start with 1), so drop it before grouping.
        let d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
        d = d.slice(0, PhoneMask.NANP_LEN);
        // Emit separators only once a digit follows them, so backspace never gets stuck on a ") ".
        if (d.length === 0)
            return '';
        if (d.length < 4)
            return `(${d}`;
        if (d.length < 7)
            return `(${d.slice(0, 3)}) ${d.slice(3)}`;
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
    /** Index in `formatted` just after `n` digits have appeared — for restoring the caret. */
    static caretForDigit(formatted, n) {
        if (n <= 0)
            return 0;
        let seen = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i])) {
                seen++;
                if (seen === n)
                    return i + 1;
            }
        }
        return formatted.length;
    }
}
